import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LFOfflineOrder,
  LFOfflineOrderDto,
  LFOfflineOrderSourceType,
  LiveForm,
  LiveFormFactory,
  ResourceData,
  SAVE_FORM_RESULT
} from '@lfw/roesberg.liveforms.core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import {
  BehaviorSubject,
  firstValueFrom,
  lastValueFrom,
  map,
  Observable,
  shareReplay,
  switchMap,
  take,
  tap
} from 'rxjs';
import { OrdersService } from '../orders.service';

const OFFLINE_ORDERS_STORE_KEY = 'offlineOrders';
const OFFLINE_ORDER_RESOURCES_STORE = 'offline-order-resources';

@Injectable({ providedIn: 'root' })
export class OfflineFormsClient {
  private items$ = new BehaviorSubject<OfflineOrders>({});
  public offlineOrders$ = this.items$.asObservable().pipe(shareReplay(1), takeUntilDestroyed());

  #dbService = inject(NgxIndexedDBService);
  readonly #ordersService = inject(OrdersService);

  constructor() {
    this.#dbService
      .getAll<LFOfflineOrderDto>(OFFLINE_ORDERS_STORE_KEY)
      .pipe(
        take(1),
        map((orders) =>
          orders.reduce((acc, curr) => {
            acc[curr.id] = new LFOfflineOrder(curr);
            return acc;
          }, {} as OfflineOrders)
        )
      )
      .subscribe((orders) => this.items$.next(orders));
  }

  async count(): Promise<number> {
    return lastValueFrom(this.#dbService.count(OFFLINE_ORDERS_STORE_KEY));
  }

  async saveOfflineOrder(form: LiveForm, sourceType: LFOfflineOrderSourceType): Promise<SAVE_FORM_RESULT> {
    return firstValueFrom(
      this.#dbService
        .update<LFOfflineOrderDto>(OFFLINE_ORDERS_STORE_KEY, LiveFormFactory.toLiveFormOfflineDto(form, sourceType))
        .pipe(
          switchMap(() => this.#dbService.getAll<LFOfflineOrderDto>(OFFLINE_ORDERS_STORE_KEY)),
          map((orders) =>
            orders.reduce((acc, curr) => {
              acc[curr.id] = new LFOfflineOrder(curr);
              return acc;
            }, {} as OfflineOrders)
          ),
          tap((orders) => this.items$.next(orders)),
          map(() => SAVE_FORM_RESULT.SAVED_OFFLINE)
        )
    );
  }

  loadResource(fileId: string): Observable<ResourceData> {
    return this.#dbService.getByKey<ResourceData>(OFFLINE_ORDER_RESOURCES_STORE, fileId).pipe(take(1));
  }

  loadResourcesByOrderId(orderId: string): Observable<ResourceData[]> {
    return this.#dbService.getAll<ResourceData>(OFFLINE_ORDER_RESOURCES_STORE).pipe(
      map((resources) => resources.filter((resource) => resource.orderId === orderId)),
      take(1)
    );
  }

  saveResource(resource: ResourceData): Observable<ResourceData> {
    return this.#dbService.update<ResourceData>(OFFLINE_ORDER_RESOURCES_STORE, resource);
  }

  async removeOfflineOrder(orderId: string, projectId: string): Promise<void> {
    if (projectId) {
      await firstValueFrom(this.#ordersService.discardCheckedOutOrder(projectId, orderId));
    }
    const resources = await firstValueFrom(this.loadResourcesByOrderId(orderId));
    await Promise.all(resources.map((resource) => this.deleteResourceByFileId(resource.fileId)));
    await firstValueFrom(this.removeOfflineOrderFromDb(orderId));
  }

  removeOfflineOrderFromDb(orderId: string): Observable<void> {
    return this.#dbService.delete<LFOfflineOrderDto>(OFFLINE_ORDERS_STORE_KEY, orderId).pipe(
      map((orders) =>
        orders.reduce((acc, curr) => {
          acc[curr.id] = new LFOfflineOrder(curr);
          return acc;
        }, {} as OfflineOrders)
      ),
      tap((orders) => this.items$.next(orders)),
      map(() => void 0)
    );
  }

  async deleteResourceByFileId(fileId: string): Promise<string> {
    await firstValueFrom(this.#dbService.delete<ResourceData>(OFFLINE_ORDER_RESOURCES_STORE, fileId));
    return fileId;
  }

  async getOfflineOrderById(orderId: string): Promise<LFOfflineOrder> {
    return firstValueFrom(this.#dbService.getByKey<LFOfflineOrderDto>(OFFLINE_ORDERS_STORE_KEY, orderId).pipe(take(1)))
      .then((dto) => new LFOfflineOrder(dto))
      .catch(() => ({}) as unknown as LFOfflineOrder);
  }
}

export interface OfflineOrdersDto {
  [key: string]: LFOfflineOrderDto;
}

export interface OfflineOrders {
  [key: string]: LFOfflineOrder;
}
